import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { ORDER_NOTE_MAX_LENGTHS } from 'constants/orderNotes';
import { takeovers as takeoverFallback } from 'constants/takeover';

import { Option } from 'types';
import { Constant } from 'types/api/api.type';
import { OrderDetails, OrderUpdatePayload } from 'types/api/orders.type';

import {
  useGetCustomerAddressesQuery,
  useGetDeliveryCodesQuery,
  useGetTakeoversQuery,
  useUpdateOrderMutation,
  useUpdateOrderNotesMutation,
} from 'redux/api/ordersApi';
import { useAppSelector } from 'redux/hooks';

import { OrderContext } from 'contexts/OrderContext';

import { formatAddressSelectOptionLabel } from 'helpers/formatAddressDisplay';
import { getApiErrorMessage } from 'helpers/getApiErrorMessage';
import { mapConstantIntoOptions } from 'helpers/mapIntoOptions';

import { formatDeliveryCodeLabel } from './components/OrderHeader/OrderHeader.hooks';
import { useFormData } from './form.data';
import { addUniqueIdsToTheItems } from './utils/addUniqueIdsToTheProducts';

export type FieldType =
  | 'note'
  | 'shippingAddressCode'
  | 'goodsDestinationCode'
  | 'deliveryCode'
  | 'orderTakenOn'
  | 'internalNote';

const useOrder = () => {
  const { id } = useParams();
  const { i18n, t } = useTranslation();
  const { season: reduxSeason, zone: settingsZone } = useAppSelector(
    (state) => state.settings
  );

  const {
    order,
    setOrder,
    infoForm,
    hasErrors,
    showErrors,
    isSend,
    isBasicInfoLoading,
    isOrderNotFound,
    basicInfo,
    customerInfo,
    customerDetails,
    orderHeader,
    scalesContent,
    setPDFData,
    setHasErrors,
    refreshOrderHeader,
    draftCustomerCode,
    setDraftCustomerCode,
    registerPersistOrderBeforeSend,
    registerSendValidation,
    missingSendFields,
  } = useContext(OrderContext);

  const { control, register, setValue, getValues } = infoForm;

  // The latest order, for callbacks that settle after a request: the `order` they captured would be stale.
  const orderRef = useRef(order);
  orderRef.current = order;

  // The latest draft customer, for the same reason: when a customer save settles, the draft may already be a
  // newer pick that has not been saved yet, and that one must survive.
  const draftCustomerRef = useRef(draftCustomerCode);
  draftCustomerRef.current = draftCustomerCode;

  // ─── RTK Query: Partial PUT mutation ───────────────────────────────────────
  const [updateOrder] = useUpdateOrderMutation();
  const [updateOrderNotes] = useUpdateOrderNotesMutation();

  // ─── Local UI state for selected values ────────────────────────────────────
  const [selectedShippingAddress, setSelectedShippingAddress] = useState<string | null>(null);
  const [selectedGoodsDestination, setSelectedGoodsDestination] = useState<string | null>(
    null
  );
  const [selectedDeliveryCode, setSelectedDeliveryCode] = useState<string | null>(null);
  const [selectedOrderTakenOn, setSelectedOrderTakenOn] = useState<string | null>(null);
  const [internalNote, setInternalNote] = useState('');
  const [orderNote, setOrderNote] = useState('');

  // ─── RTK Query: Takeovers (cached, fetched once) ───────────────────────────
  const { data: takeoverList } = useGetTakeoversQuery();

  // Last note values taken from the server, per order. A note is copied into the form only while
  // the user has not edited it, so a header change (e.g. the optimistic note patch being undone
  // after a failed save) never wipes what they typed.
  const syncedNotesRef = useRef<{
    orderId: string | null;
    note: string;
    internalNote: string;
  }>({
    orderId: null,
    note: '',
    internalNote: '',
  });

  // Sync order header → local state on first load or when header refreshes
  useEffect(() => {
    if (!orderHeader) return;

    if (draftCustomerCode && draftCustomerCode !== orderHeader.customerCode) {
      return;
    }

    setSelectedShippingAddress(orderHeader.shippingAddressCode ?? null);
    setSelectedGoodsDestination(orderHeader.goodsDestinationCode ?? null);
    setSelectedDeliveryCode(orderHeader.deliveryCode ?? null);
    setSelectedOrderTakenOn(orderHeader.orderTakenOn ?? null);

    const synced = syncedNotesRef.current;
    const isNewOrder = synced.orderId !== orderHeader.orderId;
    const syncNote = (
      formField: 'internalNote' | 'notes',
      server: string,
      lastSynced: string,
      setState: (value: string) => void
    ): string => {
      const current = (getValues(formField) as string | undefined) ?? '';
      if (isNewOrder || (server !== lastSynced && current === lastSynced)) {
        setState(server);
        setValue(formField, server);
        return server;
      }
      // Form and server agree again (a save went through): track the new value.
      return current === server ? server : lastSynced;
    };
    syncedNotesRef.current = {
      orderId: orderHeader.orderId,
      internalNote: syncNote(
        'internalNote',
        orderHeader.internalNote ?? '',
        synced.internalNote,
        setInternalNote
      ),
      note: syncNote('notes', orderHeader.note ?? '', synced.note, setOrderNote),
    };
  }, [orderHeader, draftCustomerCode, setValue, getValues, id]);

  // Reset addresses when customer changes: the local state (what Send sends) AND the form values (what the
  // selects show). Clearing only the state would leave a shared hub shown in the select while Send has none.
  // If the backend keeps an address for the new customer it comes back with the header after the save.
  useEffect(() => {
    if (!draftCustomerCode || draftCustomerCode === order?.customerCode) {
      return;
    }
    setSelectedShippingAddress(null);
    setSelectedGoodsDestination(null);
    setValue('shippingAddressCode', '');
    setValue('goodsDestinationCode', '');
  }, [draftCustomerCode, order?.customerCode, setValue]);

  // ─── RTK Query: Customer addresses (parallel, cached by customerId) ────────
  // Use customerDetails.customerId directly from RTK Query cache (avoids the
  // extra render tick caused by the useState/useEffect mapping in OrderContext).
  const customerId =
    customerDetails?.customerId ?? (customerInfo?.id ? Number(customerInfo.id) : undefined);

  // The lists below belong to the customer whose details the page holds (`customerDetails`). They are the
  // ORDER's customer's lists only while that is the saved customer. While a change is pending (and the new
  // customer's lists are already cached, so they switch in the same render as the draft), or while the saved
  // customer's details are still loading after a refusal (RTK keeps the previous customer's data until they have),
  // they are another customer's. Syncing or validating the selected addresses against them then works on a stale
  // selection and either writes the old customer's address back or wipes the saved one.
  const addressListsAreOrdersOwn =
    !customerDetails || customerDetails.customerCode === order?.customerCode;

  const { data: shippingAddresses = [] } = useGetCustomerAddressesQuery(
    { customerId: customerId!, type: 'SHIPPING' },
    { skip: !customerId }
  );

  const { data: goodsDestinationAddresses = [] } = useGetCustomerAddressesQuery(
    { customerId: customerId!, type: 'GOODS_DESTINATION' },
    { skip: !customerId }
  );

  // ─── RTK Query: Delivery codes (cached by season+linesheet) ────────────────
  const orderSeason = orderHeader?.season;
  const orderLinesheet = orderHeader?.collectionCode;

  const { data: deliveryCodes = [] } = useGetDeliveryCodesQuery(
    {
      season: orderSeason!,
      linesheet: orderLinesheet || undefined,
      isActive: true,
    },
    { skip: !orderSeason }
  );

  // Sync RHF values once address options become available (fixes the case where
  // selectedShippingAddress is set before options are loaded, causing the select
  // to appear empty until a page refresh).
  useEffect(() => {
    if (!addressListsAreOrdersOwn) return;
    if (shippingAddresses.length > 0 && selectedShippingAddress) {
      setValue('shippingAddressCode', selectedShippingAddress);
    }
  }, [shippingAddresses, selectedShippingAddress, addressListsAreOrdersOwn, setValue]);

  useEffect(() => {
    if (!addressListsAreOrdersOwn) return;
    if (goodsDestinationAddresses.length > 0 && selectedGoodsDestination) {
      setValue('goodsDestinationCode', selectedGoodsDestination);
    }
  }, [
    goodsDestinationAddresses,
    selectedGoodsDestination,
    addressListsAreOrdersOwn,
    setValue,
  ]);

  // ─── Derived options ───────────────────────────────────────────────────────

  const shippingAddressOptions: Option[] = useMemo(
    () =>
      shippingAddresses.map((addr) => ({
        value: addr.code,
        label: formatAddressSelectOptionLabel(addr),
      })),
    [shippingAddresses]
  );

  const goodsDestinationOptions: Option[] = useMemo(
    () =>
      goodsDestinationAddresses.map((addr) => ({
        value: addr.code,
        label: formatAddressSelectOptionLabel(addr),
      })),
    [goodsDestinationAddresses]
  );

  const deliveryCodeOptions: Option[] = useMemo(() => {
    const activeOptions = deliveryCodes.map((dc) => ({
      value: dc.code,
      label: formatDeliveryCodeLabel(dc, i18n.language),
    }));

    // If the saved delivery code is inactive (not in the active list),
    // include it as an option so the select displays its current value.
    if (
      selectedDeliveryCode &&
      !deliveryCodes.some((dc) => dc.code === selectedDeliveryCode)
    ) {
      activeOptions.unshift({
        value: selectedDeliveryCode,
        label: `${selectedDeliveryCode} (${t('status.inactive')})`,
      });
    }

    return activeOptions;
  }, [deliveryCodes, i18n.language, selectedDeliveryCode, t]);

  const orderTakenOnOptions: Option[] = useMemo(() => {
    const source = takeoverList && takeoverList.length > 0 ? takeoverList : takeoverFallback;
    return mapConstantIntoOptions(source as Constant[]).map((opt) => ({
      ...opt,
      label: opt.label,
    }));
  }, [takeoverList, t]);

  const selectedShippingAddressDto = useMemo(
    () => shippingAddresses.find((a) => a.code === selectedShippingAddress) ?? null,
    [shippingAddresses, selectedShippingAddress]
  );

  const selectedGoodsDestinationDto = useMemo(
    () => goodsDestinationAddresses.find((a) => a.code === selectedGoodsDestination) ?? null,
    [goodsDestinationAddresses, selectedGoodsDestination]
  );

  const customerSearchZone = useMemo(
    () => orderHeader?.zone ?? customerInfo?.zone ?? basicInfo?.zone ?? settingsZone ?? '',
    [orderHeader?.zone, customerInfo?.zone, basicInfo?.zone, settingsZone]
  );

  const isSent = orderHeader?.status === 'SEND' || basicInfo?.status?.value === 'SEND';
  const season = orderHeader?.season || basicInfo?.season || reduxSeason || '';
  const collection = orderHeader?.collectionCode || '';
  const orderZone = orderHeader?.zone || basicInfo?.zone || settingsZone || '';

  const isLoading = isBasicInfoLoading;

  // ─── Partial PUT — only send the changed field ─────────────────────────────

  const debounceTimersRef = useRef<Record<string, ReturnType<typeof setTimeout> | null>>({});
  const saveQueueRef = useRef<Promise<void>>(Promise.resolve());

  // `on` is optional and only the customer save passes it: ok / fail run after the request has settled.
  const autoSaveField = useCallback(
    (
      fieldUpdate: Partial<OrderUpdatePayload>,
      on?: { ok?: () => void; fail?: () => void }
    ) => {
      if (isSend || !id) return;

      // Chain each save onto the previous one so requests execute in order.
      saveQueueRef.current = saveQueueRef.current.then(async () => {
        const saveToastId = toast.loading(t('orderTable.savingQty'));
        try {
          await updateOrder({ orderId: id, data: fieldUpdate }).unwrap();
          toast.success(t('form.saved'), { id: saveToastId });
          on?.ok?.();
        } catch (e: unknown) {
          if (
            (e as { name?: string }).name === 'AbortError' ||
            (e as { name?: string }).name === 'ConditionError'
          ) {
            toast.dismiss(saveToastId);
            return;
          }
          toast.error(getApiErrorMessage(e, t('form.errorSaving')), { id: saveToastId });
          on?.fail?.();
        }
      });
    },
    [id, isSend, updateOrder, t]
  );

  // Save notes via a dedicated mutation that skips cache invalidation (no refetch).
  const saveNoteField = useCallback(
    (fieldUpdate: Pick<OrderUpdatePayload, 'note' | 'internalNote'>) => {
      if (isSend || !id) return;

      saveQueueRef.current = saveQueueRef.current.then(async () => {
        const saveToastId = toast.loading(t('orderTable.savingQty'));
        try {
          await updateOrderNotes({ orderId: id, data: fieldUpdate }).unwrap();
          toast.success(t('form.saved'), { id: saveToastId });
        } catch (e: unknown) {
          if (
            (e as { name?: string }).name === 'AbortError' ||
            (e as { name?: string }).name === 'ConditionError'
          ) {
            toast.dismiss(saveToastId);
            return;
          }
          toast.error(getApiErrorMessage(e, t('form.errorSaving')), { id: saveToastId });
        }
      });
    },
    [id, isSend, updateOrderNotes, t]
  );

  const updateOrderData = useCallback(
    (data: string, type: FieldType) => {
      let changed = false;
      switch (type) {
        case 'shippingAddressCode':
          if (data !== (selectedShippingAddress ?? '')) {
            setSelectedShippingAddress(data);
            changed = true;
          }
          break;
        case 'goodsDestinationCode':
          if (data !== (selectedGoodsDestination ?? '')) {
            setSelectedGoodsDestination(data);
            changed = true;
          }
          break;
        case 'deliveryCode':
          if (data !== (selectedDeliveryCode ?? '')) {
            setSelectedDeliveryCode(data);
            changed = true;
          }
          break;
        case 'orderTakenOn':
          if (data !== (selectedOrderTakenOn ?? '')) {
            setSelectedOrderTakenOn(data);
            changed = true;
          }
          break;
        case 'internalNote':
          if (data !== internalNote) {
            setInternalNote(data);
            changed = true;
          }
          break;
        case 'note':
          if (data !== orderNote) {
            setOrderNote(data);
            changed = true;
          }
          break;
        default:
          return;
      }
      if (!changed) return;

      // Route note fields through the dedicated mutation (no cache invalidation)
      if (type === 'internalNote' || type === 'note') {
        saveNoteField({ [type]: data } as Pick<OrderUpdatePayload, 'note' | 'internalNote'>);
      } else {
        autoSaveField({ [type]: data });
      }
    },
    [
      autoSaveField,
      saveNoteField,
      internalNote,
      orderNote,
      selectedDeliveryCode,
      selectedGoodsDestination,
      selectedOrderTakenOn,
      selectedShippingAddress,
    ]
  );

  // Debounced save for free-text fields (800ms after last keystroke).
  // Each field gets its own independent timer so editing one field does not
  // cancel the pending save for the other.
  const debouncedSave = useCallback(
    (data: string, type: 'internalNote' | 'note') => {
      const existing = debounceTimersRef.current[type];
      if (existing) clearTimeout(existing);
      debounceTimersRef.current[type] = null;
      // Over the API limit: nothing is saved; the field turns red with its counter.
      if (data.length > ORDER_NOTE_MAX_LENGTHS[type]) return;
      debounceTimersRef.current[type] = setTimeout(() => {
        debounceTimersRef.current[type] = null;
        updateOrderData(data, type);
      }, 800);
    },
    [updateOrderData]
  );

  // ─── Persist order before send (partial PUT) ───────────────────────────────

  const persistOrderHeader = useCallback(
    async (overrides?: Partial<OrderUpdatePayload>) => {
      if (!id) return;

      // This PUT carries every header field, notes included, read from the form below. A note timer that
      // fires later would PUT to an order that is already sent (the backend answers 409), and an earlier
      // queued save must land first. The queue never rejects: each save catches its own error.
      const pendingNoteSaves = (['note', 'internalNote'] as const).filter(
        (type) => debounceTimersRef.current[type]
      );
      Object.values(debounceTimersRef.current).forEach((timer) => {
        if (timer) clearTimeout(timer);
      });
      debounceTimersRef.current = {};
      await saveQueueRef.current;

      const notesVal = getValues('notes');
      const internalVal = getValues('internalNote');
      const codeRaw = getValues('customerCode');
      const codeFromForm = typeof codeRaw === 'string' ? codeRaw.trim() : '';

      const resolvedCustomer =
        (draftCustomerCode && draftCustomerCode.trim() !== '' ? draftCustomerCode : '') ||
        codeFromForm ||
        order?.customerCode ||
        orderHeader?.customerCode ||
        '';

      // Build only the fields that may differ from server state
      const payload: Partial<OrderUpdatePayload> = {
        customerCode: resolvedCustomer,
        shippingAddressCode: selectedShippingAddress,
        goodsDestinationCode: selectedGoodsDestination,
        orderTakenOn: selectedOrderTakenOn,
        deliveryCode: selectedDeliveryCode,
        note: typeof notesVal === 'string' ? notesVal : orderNote,
        internalNote: typeof internalVal === 'string' ? internalVal : internalNote,
        ...overrides,
      };

      try {
        await updateOrder({ orderId: id, data: payload }).unwrap();
      } catch (e: unknown) {
        // The Send failed, so the order is still a draft and the note the user typed last has not been
        // saved: put its save back, as if the timer had not been cleared. (A 409 means the order is not a
        // draft any more: nothing to save.)
        if ((e as { status?: number }).status !== 409) {
          pendingNoteSaves.forEach((type) => {
            const text = getValues(type === 'note' ? 'notes' : 'internalNote');
            if (typeof text === 'string') debouncedSave(text, type);
          });
        }
        throw e;
      }

      setDraftCustomerCode(null);
      if (order) {
        setOrder({
          ...order,
          customerCode: resolvedCustomer,
          note: payload.note ?? order.note,
        });
      }
      refreshOrderHeader();
    },
    [
      id,
      getValues,
      draftCustomerCode,
      order,
      orderHeader,
      selectedShippingAddress,
      selectedGoodsDestination,
      selectedOrderTakenOn,
      selectedDeliveryCode,
      orderNote,
      internalNote,
      updateOrder,
      debouncedSave,
      setOrder,
      setDraftCustomerCode,
      refreshOrderHeader,
    ]
  );

  useEffect(() => {
    registerPersistOrderBeforeSend(persistOrderHeader);
    return () => registerPersistOrderBeforeSend(null);
  }, [registerPersistOrderBeforeSend, persistOrderHeader]);

  // Provide a fresh snapshot for send validation
  useEffect(() => {
    registerSendValidation(() => {
      const codeRaw = getValues('customerCode');
      const codeFromForm = typeof codeRaw === 'string' ? codeRaw.trim() : '';
      const resolvedCustomer =
        (draftCustomerCode && draftCustomerCode.trim() !== '' ? draftCustomerCode : '') ||
        codeFromForm ||
        order?.customerCode ||
        orderHeader?.customerCode ||
        '';

      const payload = {
        season: orderHeader?.season,
        zone: orderHeader?.zone,
        customerCode: resolvedCustomer,
        shippingAddressCode: selectedShippingAddress,
        goodsDestinationCode: selectedGoodsDestination,
        orderTakenOn: selectedOrderTakenOn,
        deliveryCode: selectedDeliveryCode,
        collectionCode: orderHeader?.collectionCode,
      };

      return {
        payload,
        items: order?.items ?? [],
        warehouseInternalCode: orderHeader?.warehouseInternalCode,
      };
    });
    return () => registerSendValidation(null);
  }, [
    registerSendValidation,
    getValues,
    draftCustomerCode,
    order?.customerCode,
    order?.items,
    orderHeader?.season,
    orderHeader?.zone,
    orderHeader?.customerCode,
    orderHeader?.collectionCode,
    orderHeader?.warehouseInternalCode,
    selectedShippingAddress,
    selectedGoodsDestination,
    selectedOrderTakenOn,
    selectedDeliveryCode,
  ]);

  // The blur PUT settles the customer in both directions (customer-connection-fix §7.5):
  //  - saved: the picked customer becomes the saved one and the draft is cleared, so picking the previous
  //    customer again is a real change and is saved like any other; the header-sync effect is no longer
  //    blocked and shows the addresses the backend kept;
  //  - refused (blocked meanwhile, cannot order, other zone, the order has lines): the draft is cleared and the
  //    field goes back to the saved customer, with the backend's reason in the toast.
  const persistCustomerOnBlur = useCallback(() => {
    if (isSend) return;
    if (!draftCustomerCode) return;
    const code = draftCustomerCode;
    // The order this save is for: if the user moves to another order while it is in flight, the saved customer
    // must not be written onto that other order.
    const orderIdAtCall = orderRef.current?.id;
    autoSaveField(
      { customerCode: code },
      {
        // orderRef, not the `order` captured when this callback was made: lines added while the PUT was in
        // flight must not be overwritten
        // The draft is cleared only if it is still the customer this PUT was for: a newer pick made while it
        // was in flight stays, and its own blur saves it.
        ok: () => {
          if (orderRef.current && orderRef.current.id === orderIdAtCall) {
            setOrder({ ...orderRef.current, customerCode: code });
          }
          if (draftCustomerRef.current === code) setDraftCustomerCode(null);
        },
        fail: () => {
          if (draftCustomerRef.current === code) setDraftCustomerCode(null);
        },
      }
    );
  }, [autoSaveField, draftCustomerCode, isSend, setOrder, setDraftCustomerCode]);

  // Validate selected addresses exist in the fetched lists
  useEffect(() => {
    // Only against the order's own customer's lists (see addressListsAreOrdersOwn): once the change is saved or
    // refused and that customer's details are the page's, this runs as usual.
    if (!addressListsAreOrdersOwn) return;

    let nextShipping = selectedShippingAddress;
    if (
      nextShipping != null &&
      shippingAddresses.length > 0 &&
      !shippingAddresses.some((a) => a.code === nextShipping)
    ) {
      nextShipping = null;
    }

    let nextGoods = selectedGoodsDestination;
    if (
      nextGoods != null &&
      goodsDestinationAddresses.length > 0 &&
      !goodsDestinationAddresses.some((a) => a.code === nextGoods)
    ) {
      nextGoods = null;
    }

    if (nextShipping === selectedShippingAddress && nextGoods === selectedGoodsDestination) {
      return;
    }

    setSelectedShippingAddress(nextShipping);
    setSelectedGoodsDestination(nextGoods);
    setValue('shippingAddressCode', nextShipping ?? '');
    setValue('goodsDestinationCode', nextGoods ?? '');
  }, [
    shippingAddresses,
    goodsDestinationAddresses,
    selectedShippingAddress,
    selectedGoodsDestination,
    addressListsAreOrdersOwn,
    setValue,
  ]);

  const formFields = useFormData(order, customerInfo, {
    shippingAddressOptions,
    goodsDestinationOptions,
    deliveryCodeOptions,
    orderTakenOnOptions,
    selectedShippingAddress,
    selectedGoodsDestination,
    selectedDeliveryCode,
    selectedOrderTakenOn,
    season,
    collection,
    zone: orderZone,
    isSent,
  });

  // ─── Sync orderHeader items → local OrderDetails state ─────────────────────
  // (orderRef itself is declared at the top of the hook: persistCustomerOnBlur reads it)

  useEffect(() => {
    const headerItems = orderHeader?.items;
    if (!orderHeader) return;

    // First load: initialise the order from the header + items.
    if (!orderRef.current) {
      const initial: OrderDetails = {
        id: orderHeader.orderId ?? '',
        userCode: '',
        season: orderHeader.season ?? '',
        zone: orderHeader.zone ?? '',
        customerCode: orderHeader.customerCode ?? '',
        customerWarehouseCode: orderHeader.warehouseInternalCode ?? '',
        orderCreatedAt: orderHeader.createdAt ?? '',
        orderNumber: String(orderHeader.orderNumber ?? ''),
        warehouseInternalCode: {
          code: orderHeader.warehouseInternalCode ?? '',
          id: '',
          name: '',
        },
        orderSendAt: 0,
        takeoverCode: { key: orderHeader.orderTakenOn ?? '' },
        note: orderHeader.note ?? '',
        status: { key: orderHeader.status ?? '', value: orderHeader.status ?? '' },
        fixedZero1: 0,
        fixedZero2: 0,
        fixedString1: '',
        fixedString2: '',
        scaleCodes: [],
        customer: {
          code: orderHeader.customerCode ?? '',
          address: '',
          city: '',
          postCode: '',
          bankName: '',
        },
        customerWarehouse: { serialNumber: '', address: '', city: '', postCode: '' },
        items: [],
        totalQuantity: orderHeader.totalQuantity ?? 0,
        totalPrice: orderHeader.totalPrice ?? 0,
        resolvedTotals: orderHeader.resolvedTotals,
      };
      setOrder(addUniqueIdsToTheItems({ ...initial, items: headerItems ?? [] }));
      return;
    }

    // Subsequent updates: only refresh items + totals from the header.
    if (!headerItems) return;
    setOrder(
      addUniqueIdsToTheItems({
        ...orderRef.current,
        items: headerItems,
        totalQuantity: orderHeader.totalQuantity ?? orderRef.current.totalQuantity,
        totalPrice: orderHeader.totalPrice ?? orderRef.current.totalPrice,
        resolvedTotals: orderHeader.resolvedTotals ?? orderRef.current.resolvedTotals,
      })
    );
  }, [orderHeader, setOrder, id]);

  // Sync RHF "notes" from the order ONLY on initial load (when order first
  // appears). Subsequent note changes go through orderHeader sync above.
  const initialNoteSyncedRef = useRef(false);
  useEffect(() => {
    initialNoteSyncedRef.current = false;
  }, [id]);
  useEffect(() => {
    if (order && !initialNoteSyncedRef.current) {
      setValue('notes', order?.note?.trim());
      initialNoteSyncedRef.current = true;
    }
  }, [order, setValue]);

  return {
    isLoading,
    control,
    formFields,
    register,
    updateOrderData,
    debouncedSave,
    order,
    setOrder,
    draftCustomerCode,
    setDraftCustomerCode,
    hasErrors,
    showErrors,
    isSend: isSend || isSent,
    isBasicInfoLoading,
    isOrderNotFound,
    basicInfo,
    customerInfo,
    customerDetails,
    customerSearchZone,
    selectedShippingAddressDto,
    selectedGoodsDestinationDto,
    scalesContent,
    setPDFData,
    setHasErrors,
    internalNote,
    orderNote,
    orderHeader,
    persistCustomerOnBlur,
    notesFormKey: orderHeader?.orderId ?? '',
    missingSendFields,
  };
};

export { useOrder };
